import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import { notifications } from "@mantine/notifications";
import {
  abbreviationsService,
  Abbreviation,
  CreateAbbreviationInput,
  UpdateAbbreviationInput,
} from "../../services/admin/abbreviations.service";

export function useAbbreviationsAdmin() {
  return useQuery<Abbreviation[]>({
    queryKey: ["admin", "abbreviations"],
    queryFn: () => abbreviationsService.getAll(),
  });
}

export function useCreateAbbreviation() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: (input: CreateAbbreviationInput) =>
      abbreviationsService.create(input),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["admin", "abbreviations"] });
      queryClient.invalidateQueries({ queryKey: ["abbreviations"] });
      notifications.show({
        title: "Success",
        message: "Abbreviation created successfully",
        color: "green",
      });
    },
    onError: (error: any) => {
      const message =
        error?.response?.data?.error ||
        error?.response?.data?.message ||
        error.message ||
        "Failed to create abbreviation";
      notifications.show({
        title: "Error",
        message,
        color: "red",
      });
    },
  });
}

export function useUpdateAbbreviation() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: (input: UpdateAbbreviationInput) =>
      abbreviationsService.update(input),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["admin", "abbreviations"] });
      queryClient.invalidateQueries({ queryKey: ["abbreviations"] });
      notifications.show({
        title: "Success",
        message: "Abbreviation updated successfully",
        color: "green",
      });
    },
    onError: (error: any) => {
      const message =
        error?.response?.data?.error ||
        error?.response?.data?.message ||
        error.message ||
        "Failed to update abbreviation";
      notifications.show({
        title: "Error",
        message,
        color: "red",
      });
    },
  });
}

export function useToggleAbbreviationActive() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: ({ id, isActive }: { id: string; isActive: boolean }) =>
      abbreviationsService.update({ id, isActive }),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["admin", "abbreviations"] });
      queryClient.invalidateQueries({ queryKey: ["abbreviations"] });
      notifications.show({
        title: "Success",
        message: "Status updated successfully",
        color: "green",
      });
    },
    onError: (error: any) => {
      const message =
        error?.response?.data?.error ||
        error?.response?.data?.message ||
        error.message ||
        "Failed to update status";
      notifications.show({
        title: "Error",
        message,
        color: "red",
      });
    },
  });
}

export function useDeleteAbbreviation() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: (id: string) => abbreviationsService.delete(id),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["admin", "abbreviations"] });
      queryClient.invalidateQueries({ queryKey: ["abbreviations"] });
      notifications.show({
        title: "Success",
        message: "Abbreviation deleted successfully",
        color: "green",
      });
    },
    onError: (error: any) => {
      const message =
        error?.response?.data?.error ||
        error?.response?.data?.message ||
        error.message ||
        "Failed to delete abbreviation";
      notifications.show({
        title: "Error",
        message,
        color: "red",
      });
    },
  });
}
